/**
 * **The one transform that turns a contact form's working value into the shape
 * `CreateContactRequestSchema` accepts** (VALE-632).
 *
 * It lived inline in `ContactFormCard.onSubmit` while that card was the only
 * surface that created a contact. Converting Add Contact onto the attribute
 * treatment gave it a second caller, and the acceptance criterion for that
 * conversion is that *"a created contact is identical to one created by the old
 * form"* — which a second, hand-copied transform could only satisfy until the
 * first time one of them changed. So it is extracted rather than re-typed, and
 * both surfaces call this.
 *
 * Every rule below is carried across verbatim from that original, including the
 * reasoning comments, because each one is a real backend or product constraint
 * rather than housekeeping:
 *
 * · **The outgoing type's fields are dropped, not emptied.** Which branch a
 *   contact is on is derived from `organisationName`, so shipping an
 *   organisation's `dateOfBirth` — or an individual's `organisationName` —
 *   would file a record that reads as both.
 * · **`isMinor` is a form-only flag.** The record stores `isOfAge`; the read-mode
 *   display and the editors bind to `isMinor`, and this is where the conversion
 *   back happens.
 * · **Empty strings become `null`.** The backend's own `emptyStringToNull` does
 *   this for a value that arrives, but an untouched optional field arrives as
 *   `''` from a form and as `undefined` from a buffer — both mean "not set".
 *
 * It is deliberately **pure** and takes the contact type as an argument rather
 * than deriving it: on the attribute create form a still-*pending* type switch
 * is exactly the state where the buffer and the reader's choice disagree, and
 * the caller is the only one that knows which of the two the reader meant.
 */

import type { Contact } from '@/lib/services/contacts/types';

export function toContactCreatePayload(
  value: Partial<Contact>,
  contactType: 'individual' | 'organisation'
): Partial<Contact> {
  const transformedValue: Partial<Contact> = { ...value };

  if (contactType === 'organisation') {
    delete transformedValue.firstName;
    delete transformedValue.middleName;
    delete transformedValue.lastName;
    delete transformedValue.alias;
    delete transformedValue.preferredName;
    delete transformedValue.dateOfBirth;
    delete transformedValue.dateOfDeath;
    // An organisation can't be deceased — drop the individual-only flag.
    delete transformedValue.isDeceased;
  } else {
    delete transformedValue.organisationName;
    // The date of death is only meaningful for a contact flagged deceased.
    // If the checkbox is unticked, never persist a stale date (it may linger
    // in form state from a since-hidden field).
    if (!transformedValue.isDeceased) {
      transformedValue.dateOfDeath = null;
    }
  }

  if (typeof transformedValue.isMinor === 'boolean') {
    transformedValue.isOfAge = !transformedValue.isMinor;
  }
  delete transformedValue.isMinor;

  if (
    transformedValue.isOfAge === null ||
    transformedValue.isOfAge === undefined
  ) {
    delete transformedValue.isOfAge;
  }

  if (transformedValue.email === '' || transformedValue.email === undefined) {
    transformedValue.email = null;
  }

  if (
    transformedValue.phoneNumber === '' ||
    transformedValue.phoneNumber === undefined
  ) {
    transformedValue.phoneNumber = null;
  }

  if (
    transformedValue.dateOfBirth === '' ||
    transformedValue.dateOfBirth === undefined
  ) {
    transformedValue.dateOfBirth = null;
  }

  if (
    transformedValue.dateOfDeath === '' ||
    transformedValue.dateOfDeath === undefined
  ) {
    transformedValue.dateOfDeath = null;
  }

  return transformedValue;
}
